"use client";

import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

type ModalType = "alert" | "confirm" | "prompt";

interface ModalOptions {
  title: string;
  message?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  placeholder?: string;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

interface ModalState extends ModalOptions {
  type: ModalType;
  resolve: (value: any) => void;
  isOpen: boolean;
}

export const GlobalModal = () => {
  const t = useTranslations();
  const [modal, setModal] = useState<ModalState | null>(null);
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (modal?.defaultValue) {
      setInputValue(modal.defaultValue);
    } else {
      setInputValue("");
    }
  }, [modal]);

  useEffect(() => {
    const handleModalUpdate = (event: CustomEvent<ModalState | null>) => {
      setModal(event.detail);
    };

    document.addEventListener(
      "modal-update",
      handleModalUpdate as EventListener
    );

    return () => {
      document.removeEventListener(
        "modal-update",
        handleModalUpdate as EventListener
      );
    };
  }, []);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && modal) {
      if (modal.type === "prompt") {
        modal.resolve(null);
      } else {
        modal.resolve(false);
      }
      setModal(null);
    }

    modal?.onOpenChange?.(isOpen);
  };

  const handleConfirm = () => {
    if (modal) {
      if (modal.type === "prompt") {
        modal.resolve(inputValue);
      } else {
        modal.resolve(true);
      }
      setModal(null);
    }
  };

  const handleCancel = () => {
    if (modal) {
      if (modal.type === "prompt") {
        modal.resolve(null);
      } else {
        modal.resolve(false);
      }
      setModal(null);
    }
  };

  if (!modal) return null;

  return (
    <Modal
      isOpen={modal.isOpen}
      onOpenChange={handleOpenChange}
      placement="center"
    >
      <ModalContent>
        {modal.title && <ModalHeader>{modal.title}</ModalHeader>}
        <ModalBody>
          {modal.message && <p>{modal.message}</p>}
          {modal.type === "prompt" && (
            <Input
              autoFocus
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={modal.placeholder}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleConfirm();
                }
              }}
            />
          )}
        </ModalBody>
        <ModalFooter>
          {modal.type !== "alert" && (
            <Button variant="flat" onPress={handleCancel}>
              {modal.cancelText || "Cancel"}
            </Button>
          )}
          <Button color="primary" onPress={handleConfirm}>
            {modal.confirmText || "OK"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Create a modal controller that will be used by the application
class ModalController {
  private static modalStateChangeCallback:
    | ((state: ModalState | null) => void)
    | null = null;

  static setModalStateChangeCallback(
    callback: (state: ModalState | null) => void
  ) {
    this.modalStateChangeCallback = callback;
  }

  private static createModal(
    type: ModalType,
    options: ModalOptions
  ): Promise<any> {
    return new Promise((resolve) => {
      if (this.modalStateChangeCallback) {
        this.modalStateChangeCallback({
          ...options,
          type,
          resolve,
          isOpen: true,
        });
      }
    });
  }

  static alert(
    title: string,
    options: Partial<ModalOptions> = {}
  ): Promise<true> {
    return this.createModal("alert", { title, ...options }) as Promise<true>;
  }

  static confirm(
    title: string,
    options: Partial<ModalOptions> = {}
  ): Promise<boolean> {
    return this.createModal("confirm", {
      title,
      ...options,
    }) as Promise<boolean>;
  }

  static prompt(
    title: string,
    options: Partial<ModalOptions> = {}
  ): Promise<string | null> {
    return this.createModal("prompt", { title, ...options }) as Promise<
      string | null
    >;
  }
}

export function ModalProvider() {
  useEffect(() => {
    const callback = (state: ModalState | null) => {
      window.dispatchEvent(
        new CustomEvent("modal-state-change", { detail: state })
      );
    };

    ModalController.setModalStateChangeCallback(callback);

    const handleModalStateChange = (event: CustomEvent<ModalState | null>) => {
      document.dispatchEvent(
        new CustomEvent("modal-update", { detail: event.detail })
      );
    };

    window.addEventListener(
      "modal-state-change",
      handleModalStateChange as EventListener
    );

    return () => {
      window.removeEventListener(
        "modal-state-change",
        handleModalStateChange as EventListener
      );
    };
  }, []);

  return null;
}

interface DialogOptions {
  title: string;
  message?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  placeholder?: string;
}

export const dialog = {
  alert: (
    title: string,
    options: Partial<DialogOptions> = {}
  ): Promise<true> => {
    return modal.alert(title, options);
  },

  confirm: (
    title: string,
    options: Partial<DialogOptions> = {}
  ): Promise<boolean> => {
    return modal.confirm(title, options);
  },

  prompt: (
    title: string,
    options: Partial<DialogOptions> = {}
  ): Promise<string | null> => {
    return modal.prompt(title, options);
  },
};

export const modal = {
  alert: (title: string, options: Partial<ModalOptions> = {}) =>
    ModalController.alert(title, options),
  confirm: (title: string, options: Partial<ModalOptions> = {}) =>
    ModalController.confirm(title, options),
  prompt: (title: string, options: Partial<ModalOptions> = {}) =>
    ModalController.prompt(title, options),
};
